import React, { useState, useEffect, useRef } from 'react'
import {
  AuthenticatedUser,
  ChatRoom,
} from '../../../clients/kyousou_api_client'
import { FirestoreUserMessage } from '../../../types/chat'
import { formatChatDateTime } from '../../../utils/datetime_util'
import { isImageFile } from '../../../utils/file_util'
import DeleteMessageModal from './delete_message_modal'
import { useFileDownload } from '../../../hooks/use_file_download'

interface Props {
  chat: ChatRoom
  currentUser: AuthenticatedUser
  message: FirestoreUserMessage
  onDelete: (messageId: string) => Promise<void>
}

export const ChatUserMessage: React.FC<Props> = ({
  message,
  chat,
  currentUser,
  onDelete,
}) => {
  const isOwn =
    message.senderEnterpriseId === Number(currentUser.kuchikomiEnterpriseId)
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { getDownloadUrl } = useFileDownload()

  // メニューの外側をクリックしたら閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  const handleDeleteClick = () => {
    setShowMenu(false)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false)

    try {
      await onDelete(message.id)
    } catch (error) {
      console.error('メッセージの削除に失敗しました:', error)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
  }

  return (
    <div
      key={message.id}
      className={`flex mb-4 ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`flex items-start max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}
      >
        {!isOwn && (
          <img
            src={chat.logo}
            alt={chat.name}
            className="w-8 h-8 rounded-full mr-2 object-cover"
          />
        )}
        <div
          className={`relative group ${isOwn ? 'flex flex-col items-end' : ''}`}
        >
          {/* 編集・削除メニュー */}
          {isOwn && !message.isPending && !message.isEditing && (
            <div
              ref={menuRef}
              className={`absolute ${isOwn ? 'right-0' : 'left-0'} top-0 -mt-5`}
            >
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 flex items-center justify-center"
              >
                <span className="material-icons !text-lg text-main-darkgray leading-none">
                  more_horiz
                </span>
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-1 bg-main-white border border-main-gray rounded-lg shadow-lg z-10 min-w-[120px]">
                  <button
                    onClick={handleDeleteClick}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-main-lightgray text-main-red flex items-center gap-2"
                  >
                    <span className="material-icons text-sm">delete</span>
                    削除
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            className={`inline-block px-4 py-3 rounded-lg ${
              isOwn
                ? 'bg-main-green text-main-white'
                : 'bg-main-gray border border-main-gray'
            } ${message.isPending || message.isEditing ? 'opacity-70' : ''}`}
          >
            {/* 1. メッセージテキスト */}
            {message.content && (
              <p className="text-sm whitespace-break-spaces break-all">
                {message.content}
              </p>
            )}

            {/* 2. ファイル（画像以外） */}
            {message.attachments
              .filter((attachment) => !isImageFile(attachment.url))
              .map((attachment, index) => {
                const marginClass =
                  index === 0 ? (message.content ? 'mt-2' : '') : 'mt-2'

                return (
                  <a
                    key={`file-${index}`}
                    href={getDownloadUrl(attachment.url, attachment.fileName)}
                    className={`${marginClass} flex items-center gap-3 px-3 py-2 ${
                      isOwn
                        ? 'bg-[rgba(68,135,107,1)] hover:opacity-80 transition-opacity duration-300'
                        : 'bg-[rgba(209,209,209,1)] hover:opacity-80 transition-opacity duration-300'
                    } rounded-lg transition-colors cursor-pointer`}
                  >
                    <span
                      className={`material-icons text-xl ${
                        isOwn ? 'text-main-white' : 'text-main-darkgray'
                      }`}
                    >
                      description
                    </span>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm truncate ${
                          isOwn ? 'text-main-white' : 'text-main-black'
                        }`}
                      >
                        {attachment.fileName}
                      </p>
                    </div>
                    <span
                      className={`material-icons text-xl ${
                        isOwn ? 'text-main-white' : 'text-main-darkgray'
                      }`}
                    >
                      download
                    </span>
                  </a>
                )
              })}

            {/* 3. 画像 */}
            {message.attachments
              .filter((attachment) => isImageFile(attachment.url))
              .map((attachment, index) => {
                const marginClass =
                  index === 0
                    ? message.content ||
                      message.attachments.some((a) => !isImageFile(a.url))
                      ? 'mt-2'
                      : ''
                    : 'mt-2'

                return (
                  <div
                    key={`image-${index}`}
                    className={`${marginClass} flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className="relative group max-w-full">
                      <img
                        src={attachment.url}
                        alt={attachment.fileName}
                        className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                        onClick={() => window.open(attachment.url, '_blank')}
                      />
                      <a
                        href={getDownloadUrl(
                          attachment.url,
                          attachment.fileName
                        )}
                        className="absolute top-2 right-2 w-7 h-7 bg-main-white border-2 border-main-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 hover:!opacity-80 transition-opacity duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.7)] cursor-pointer"
                      >
                        <span className="material-icons text-lg text-main-green">
                          download
                        </span>
                      </a>
                    </div>
                  </div>
                )
              })}
          </div>
          <p
            className={`text-xs text-main-darkgray mt-1 ${
              isOwn ? 'text-right' : 'text-left'
            }`}
          >
            {isOwn ? (
              <>
                {message.isEdited && (
                  <span className="mr-1 text-main-darkgray">(編集済み)</span>
                )}
                {formatChatDateTime(message.createdAt)}
              </>
            ) : (
              <>
                {formatChatDateTime(message.createdAt)}
                {message.isEdited && (
                  <span className="ml-1 text-main-darkgray">(編集済み)</span>
                )}
              </>
            )}
          </p>
        </div>
      </div>

      {/* 削除確認モーダル */}
      <DeleteMessageModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        messageContent={message.content}
      />
    </div>
  )
}
